import { useState } from 'react';

import type {
  CommandPaletteContext,
  CommandPaletteScope,
  CommandPaletteScopeDefinition,
} from '../command-palette/command-palette.models';
import { useLazyVulnerabilityLookupQuery } from '../quick-search.api';

import type { VulnerabilitySearchResultProps } from './vulnerability-search-result';
import VulnerabilitySearchResult from './vulnerability-search-result';
import {
  getExactMatch,
  vulnerabilitySearchOutcome,
} from './vulnerability-search.utils';

/**
 * The vulnerability search scope: turns the palette query into a vulnerability
 * lookup with a loading/results/error state machine, and maps each result to
 * its CVE detail route.
 */
export const useVulnerabilitySearchScope = (
  ctx: CommandPaletteContext,
): CommandPaletteScope => {
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [lookupVulnerability, { data, error, isFetching }] =
    useLazyVulnerabilityLookupQuery();

  const vulnerabilities = data?.vulnerabilities ?? [];
  const exactMatch = error
    ? null
    : getExactMatch(submittedQuery, vulnerabilities);

  // Gated on `query === submittedQuery` so editing the input reverts Enter to a
  // fresh search instead of opening a now-stale row.
  const hasNavigableResults =
    !!submittedQuery &&
    ctx.query.trim() === submittedQuery &&
    vulnerabilities.length > 0;

  const showOptions =
    hasNavigableResults && !isSearching && !error && !exactMatch;

  const searchVulnerability = (id: string) => {
    setSubmittedQuery(id);
    setIsSearching(true);
    lookupVulnerability(id);
  };

  const handleSearchComplete = () => {
    // An exact single match skips the list and forwards straight to its page.
    if (exactMatch) {
      ctx.activate(vulnerabilitySearchOutcome(exactMatch.vulnerabilityId));
      return;
    }

    setIsSearching(false);
    ctx.onSettle();
  };

  const resultProps = (): VulnerabilitySearchResultProps => {
    if (isSearching) {
      return {
        loading: isFetching,
        onComplete: handleSearchComplete,
        status: 'searching',
      };
    }

    if (!submittedQuery) return { status: 'idle' };

    if (error) {
      return {
        error,
        onRetry: () => searchVulnerability(submittedQuery),
        status: 'error',
      };
    }

    if (data && !data.vulnerabilities?.length) {
      return {
        builtAt: data.grypedbMetadata.builtAt ?? null,
        query: submittedQuery,
        status: 'noData',
      };
    }

    if (vulnerabilities.length) {
      return {
        activeIndex: ctx.activeIndex,
        listboxId: ctx.listboxId,
        onActivate: ctx.onActivate,
        onSelect: ctx.close,
        query: submittedQuery,
        status: 'results',
        vulnerabilities,
      };
    }

    return { status: 'idle' };
  };

  return {
    content: <VulnerabilitySearchResult {...resultProps()} />,
    inputLabel: 'Search vulnerabilities',
    onSubmit: () => {
      // Not gated on isFetching: a submit after a mid-lookup close must still
      // fire (RTKQ dedupes pending args).
      const id = ctx.query.trim();
      if (id) searchVulnerability(id);
    },
    optionCount: vulnerabilities.length,
    outcomeAt: index => {
      const vulnerability = vulnerabilities[index];
      return vulnerability
        ? vulnerabilitySearchOutcome(vulnerability.vulnerabilityId)
        : undefined;
    },
    placeholder: 'Enter full vulnerability ID…',
    showOptions,
  };
};

/**
 * The vulnerability scope definition, ready to hand to the command palette.
 */
export const vulnerabilitySearchScope: CommandPaletteScopeDefinition = {
  key: 'vulnerability',
  label: 'Vulnerability',
  useScope: useVulnerabilitySearchScope,
};
